// DEPLOY: /var/www/woody.wtf/frontend/src/components/SmcChart.js
// SMC Lab chart: one ticker's entry/execution candles with what the model saw.
// Top-down: bias and entry BOS/CHoCH, pullback zones, setups, trades, bias shading.
// Raid & shift: liquidity levels, raids, shifts, the entry gap, setups, trades.
// Each layer only draws if the payload has it; setups thrown out show as grey dots.
// Data: the "chart" payload of engines/smc_backtest.py. Tap a legend item to hide
// a layer. The price axis fits the candles in view (levels far away don't squash
// it). Times are New York.
import React, { useEffect, useRef, useState } from 'react';

const HC = window.Highcharts;   // Highcharts Stock via CDN (public/index.html)
const SRC = { prev_day: 'PD', prev_week: 'PW', overnight: 'ON', opening_range: 'OR', equal: 'EQ', htf_swing: 'SW' };
const GAP = { 1: 'rgba(22,163,74,0.20)', '-1': 'rgba(220,38,38,0.20)' };
const ZONE = { 1: 'rgba(22,163,74,0.12)', '-1': 'rgba(220,38,38,0.12)' };
const TREND = { 1: 'rgba(22,163,74,0.05)', '-1': 'rgba(220,38,38,0.05)' };
const fmtT = ms => HC.dateFormat('%a %e %b %H:%M', ms);
const srcLabel = src => [].concat(src).map(x => SRC[x] || x).join('/');

// Layers share one legend entry: the first series of a group carries the name.
function grouped(list, id, name, make) {
  return list.map((x, i) => ({ ...make(x), ...(i === 0 ? { id, name } : { linkedTo: id, name }) }));
}

// Fit the price axis to the candles (plus visible setup entries/stops) in the x range.
function fit(chart, c) {
  const { min, max } = chart.xAxis[0].getExtremes();
  let lo = Infinity, hi = -Infinity;
  c.candles.forEach(k => { if (k[0] >= min && k[0] <= max) { lo = Math.min(lo, k[3]); hi = Math.max(hi, k[2]); } });
  c.setups.forEach(s => { if (s.x1 >= min && s.x0 <= max && s.entry != null && s.stop != null) { lo = Math.min(lo, s.entry, s.stop); hi = Math.max(hi, s.entry, s.stop); } });
  if (lo === Infinity) return;
  const pad = (hi - lo) * 0.06 || 0.5;
  chart.yAxis[0].setExtremes(lo - pad, hi + pad, true, false);
}

function options(c) {
  const close = c.candles.map(k => [k[0], k[4]]);
  const nearestClose = ms => { let v = close[0][1]; for (const [x, y] of close) { if (x <= ms) v = y; else break; } return v; };
  const line = (x0, x1, y, color, dash = 'Solid', width = 1) => ({ type: 'line', data: [[x0, y], [x1, y]], color, dashStyle: dash, lineWidth: width, marker: { enabled: false }, enableMouseTracking: false });

  const series = [{ type: 'candlestick', id: 'main', name: c.ticker, data: c.candles, color: '#cc0000', upColor: '#00aa00', lineColor: '#000', upLineColor: '#000' }];

  series.push(...grouped(c.levels, 'lvl', 'Liquidity', l => ({
    ...line(l.x0, l.x1, l.price, l.swept ? '#7C3AED' : '#9CA3AF', 'Dash'),
    dataLabels: { enabled: true, formatter() { return this.point.index === 1 ? `${srcLabel(l.src)}${l.side === 'high' ? 'H' : 'L'}` : null; }, style: { fontSize: '9px', fontWeight: 'normal', color: '#6B7280', textOutline: 'none' } } })));
  series.push(...grouped(c.zones || [], 'zn', 'Zones', z => ({
    type: 'area', data: [[z.x0, z.top], [z.x1, z.top]], threshold: z.bottom, color: ZONE[z.dir], fillColor: ZONE[z.dir],
    lineWidth: 0, marker: { enabled: false }, enableMouseTracking: false })));
  series.push(...grouped(c.breaks || [], 'brk', 'BOS / CHoCH', b => ({
    ...line(b.x0, b.x1, b.price, b.dir === 1 ? '#16A34A' : '#DC2626', 'Solid', b.tf === 'bias' ? 3 : 1),
    dataLabels: { enabled: true, formatter() { return this.point.index === 1 ? (b.tf === 'bias' ? `${b.kind} (bias)` : b.kind) : null; }, style: { fontSize: b.tf === 'bias' ? '10px' : '8px', fontWeight: b.tf === 'bias' ? 'bold' : 'normal', color: b.dir === 1 ? '#16A34A' : '#DC2626', textOutline: 'none' } } })));
  series.push(...grouped([c.sweeps], 'swp', 'Raids', sw => ({
    type: 'scatter', color: '#7C3AED', marker: { symbol: 'diamond' }, data: sw.map(p => ({ x: p.x, y: p.price, info: `Raid of ${srcLabel(p.src)} ${p.dir === 1 ? 'low' : 'high'} ${p.level.toFixed(2)} (wick to ${p.price.toFixed(2)})`, marker: { symbol: 'diamond', radius: 5, fillColor: '#7C3AED' } })),
    tooltip: { pointFormatter() { return this.options.info; } } })));
  series.push(...grouped(c.shifts, 'sh', 'Shifts', x => ({
    ...line(x.x0, x.x1, x.price, x.dir === 1 ? '#16A34A' : '#DC2626', 'Solid', 1.5),
    dataLabels: { enabled: true, formatter() { return this.point.index === 1 ? 'MSS' : null; }, style: { fontSize: '9px', color: x.dir === 1 ? '#16A34A' : '#DC2626', textOutline: 'none' } } })));
  // Entry gaps: 'area' filled down to threshold = gap bottom (core Highstock, no extra module).
  series.push(...grouped(c.setups.filter(s => s.gap), 'gap', 'Entry gaps', s => ({
    type: 'area', data: [[Math.min(s.gap.start, s.x0), s.gap.top], [s.x1, s.gap.top]], threshold: s.gap.bottom,
    color: GAP[s.dir], fillColor: GAP[s.dir], lineWidth: 0, marker: { enabled: false }, enableMouseTracking: false })));
  const setupLines = c.setups.flatMap(s => {
    const w = s.outcome === 'filled' ? 2 : 1;
    const what = s.sweep_src ? `after raid of ${srcLabel(s.sweep_src)}` : 'with the bias';
    const entry = s.entry != null ? s.entry : null;
    if (s.stop == null) return [];
    const tip = `${s.dir === 1 ? 'Long' : 'Short'} ${what} · entry ${entry != null ? entry.toFixed(2) : 'at market'} stop ${s.stop.toFixed(2)}${s.target != null ? ` target ${s.target.toFixed(2)}` : ''}${s.rr ? ` (R:R ${s.rr})` : ''} · ${s.outcome}`;
    return [
      ...(entry != null ? [{ ...line(s.x0, s.x1, entry, '#374151', 'Solid', w), enableMouseTracking: true, tooltip: { pointFormat: tip } }] : []),
      { ...line(s.x0, s.x1, s.stop, '#DC2626', 'ShortDot', w), ...(entry == null ? { enableMouseTracking: true, tooltip: { pointFormat: tip } } : {}) },
      ...(s.target != null ? [line(s.x0, s.x1, s.target, '#16A34A', 'ShortDot', w)] : []),
    ];
  });
  series.push(...grouped(setupLines, 'set', 'Setups', x => x));
  series.push(...grouped([c.rejected], 'rej', 'Thrown out', r => ({
    type: 'scatter', color: '#9CA3AF', marker: { symbol: 'circle' }, data: r.map(p => ({ x: p.x, y: p.entry, info: `${p.dir === 1 ? 'Long' : 'Short'} setup thrown out: ${p.why}`, marker: { symbol: 'circle', radius: 4, fillColor: '#9CA3AF', lineColor: '#6B7280', lineWidth: 1 } })),
    tooltip: { pointFormatter() { return this.options.info; } } })));
  const tradePts = c.trades.flatMap(t => {
    const ein = Date.parse(t.entry_ts), eout = Date.parse(t.exit_ts);
    const yOut = t.exit_reason === 'stop' ? t.stop : t.exit_reason === 'target' ? t.target : nearestClose(eout);
    const yIn = t.entry_sig != null ? t.entry_sig : t.entry;
    const info = `${t.dir === 1 ? 'Long' : 'Short'} ${t.shares} ${t.vehicle}<br/>In ${fmtT(ein)} @ ${t.entry_px} · out ${fmtT(eout)} @ ${t.exit_px} (${t.exit_reason})<br/>${t.r.toFixed(2)}R · $${Math.round(t.pnl)}`;
    return [{ x: ein, y: yIn, info, marker: { symbol: t.dir === 1 ? 'triangle' : 'triangle-down', radius: 7, fillColor: '#2563EB' } },
            { x: eout, y: yOut, info, marker: { symbol: 'circle', radius: 6, fillColor: t.r > 0 ? '#16A34A' : '#DC2626' } }];
  });
  series.push(...grouped([tradePts], 'trd', 'Trades', pts => ({ type: 'scatter', color: '#2563EB', marker: { symbol: 'triangle' }, data: pts, zIndex: 10, tooltip: { pointFormatter() { return this.options.info; } } })));

  return {
    credits: { enabled: false }, title: { text: '' }, rangeSelector: { enabled: false },
    stockTools: { gui: { enabled: false } },
    time: { timezone: 'America/New_York' },
    chart: { panning: { enabled: true, type: 'x' }, zooming: { type: 'x', pinchType: 'x' }, events: { load() { fit(this, c); } } },
    legend: { enabled: true, itemStyle: { fontSize: '11px' } },
    tooltip: { split: false, shared: false, xDateFormat: '%a %e %b %H:%M NY' },
    xAxis: { ordinal: true, plotBands: c.trend.filter(b => b.dir).map(b => ({ from: b.x0, to: b.x1, color: TREND[b.dir] })),
             events: { afterSetExtremes() { fit(this.chart, c); } } },
    yAxis: [{ labels: { align: 'left' }, startOnTick: false, endOnTick: false }],
    plotOptions: { series: { dataGrouping: { enabled: false }, stickyTracking: false, states: { inactive: { opacity: 1 } } } },
    series: series.filter(x => x.data && x.data.length),
  };
}

export default function SmcChart({ chart }) {
  const ref = useRef(null);
  const hc = useRef(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!HC || !ref.current || !chart) return;
    // a bad payload shows a message instead of blanking the whole page
    try { setErr(''); hc.current = HC.stockChart(ref.current, options(chart)); }
    catch (e) { setErr(`Chart could not be drawn: ${e.message}`); hc.current = null; }
    return () => { hc.current?.destroy(); hc.current = null; };
  }, [chart]);
  return (
    <div>
      {err && <div style={{ color: 'var(--danger, #DC2626)', fontSize: '13px', marginBottom: '6px' }}>{err}</div>}
      <button style={{ padding: '4px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '12px', background: 'var(--bg)', color: 'var(--text)', marginBottom: '6px' }}
              onClick={() => hc.current?.fullscreen?.toggle()}>⤢ Full screen</button>
      <div ref={ref} style={{ height: 'min(80vh, 720px)', minHeight: '440px' }} />
    </div>
  );
}
